import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  makeTempId,
  buildChoiceLocalizedDetails,
  getPrimaryMenuLanguage,
  getMenuOptionalLanguages,
  getProductDisplayTitle,
  getChoiceDisplayTitle,
  getItemDisplayTitle,
} from "./utils";
import { showWizardToast, showWizardValidation } from "../wizardFeedback";
import { formatMoney } from "../../../../../utils/currency";
import { buildChoiceFormLivePreview } from "../../../Cms/MenuTree/menuTreeUtils";

function countProductsForChoice(choiceId, productChoices) {
  let count = 0;
  Object.values(productChoices || {}).forEach((ids) => {
    if (Array.isArray(ids) && ids.includes(choiceId)) count += 1;
  });
  return count;
}

function getProductIdsForChoice(choiceId, productChoices) {
  return Object.entries(productChoices || {})
    .filter(([, ids]) => Array.isArray(ids) && ids.includes(choiceId))
    .map(([productId]) => productId);
}

function syncChoiceProductAssignments(productChoices, choiceId, selectedProductIds) {
  const next = { ...(productChoices || {}) };
  const selected = new Set((selectedProductIds || []).filter(Boolean));

  Object.keys(next).forEach((productId) => {
    const current = Array.isArray(next[productId]) ? next[productId] : [];
    if (selected.has(productId)) {
      if (!current.includes(choiceId)) next[productId] = [...current, choiceId];
      return;
    }
    const filtered = current.filter((id) => id !== choiceId);
    if (filtered.length) next[productId] = filtered;
    else delete next[productId];
  });

  selected.forEach((productId) => {
    if (!next[productId]) next[productId] = [choiceId];
  });

  return next;
}

const EMPTY_REL = Object.freeze({});

const Step3Choices = ({
  draft,
  setDraft,
  goNext,
  goPrev,
  setLiveDetailCandidate,
  livePreviewProductId = null,
}) => {
  const { t } = useTranslation();
  const menuPrimaryLanguage = getPrimaryMenuLanguage(draft?.menu);
  const menuOptionalLanguages = getMenuOptionalLanguages(draft?.menu);
  const products = Array.isArray(draft?.products) ? draft.products : [];
  const choices = Array.isArray(draft?.choices) ? draft.choices : [];
  const items = Array.isArray(draft?.items) ? draft.items : [];
  const productChoices = draft?.relations?.productChoices || EMPTY_REL;
  const choiceItems = draft?.relations?.choiceItems || EMPTY_REL;

  const [title, setTitle] = useState("");
  const [minSelected, setMinSelected] = useState("0");
  const [maxSelected, setMaxSelected] = useState("1");
  const [editingChoiceId, setEditingChoiceId] = useState(null);
  const [selectedChoiceIds, setSelectedChoiceIds] = useState(() => new Set());
  const [choicesOpen, setChoicesOpen] = useState(true);
  const [choicesQuery, setChoicesQuery] = useState("");
  const [assignProductIds, setAssignProductIds] = useState(() => new Set());
  const [assignProductQuery, setAssignProductQuery] = useState("");
  const [assignDropdownOpen, setAssignDropdownOpen] = useState(false);
  const choiceFormRef = useRef(null);
  const assignDropdownRef = useRef(null);

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        id: product.tempId,
        title: getProductDisplayTitle(product, menuPrimaryLanguage),
        isPreview: product.tempId === livePreviewProductId,
      })),
    [products, menuPrimaryLanguage, livePreviewProductId]
  );
  const selectedPreviewProduct = useMemo(
    () => productOptions.find((product) => product.isPreview) || null,
    [productOptions]
  );
  const showAssignToSelectedProduct = Boolean(selectedPreviewProduct);
  const moreProductOptions = useMemo(
    () => productOptions.filter((product) => !product.isPreview),
    [productOptions]
  );
  const selectedAssignProducts = useMemo(
    () => productOptions.filter((product) => assignProductIds.has(product.id)),
    [productOptions, assignProductIds]
  );
  const filteredMoreProducts = useMemo(() => {
    const query = assignProductQuery.trim().toLowerCase();
    if (!query) return moreProductOptions;
    return moreProductOptions.filter((product) =>
      String(product.title || "")
        .toLowerCase()
        .includes(query)
    );
  }, [moreProductOptions, assignProductQuery]);

  const filteredChoices = useMemo(() => {
    const query = choicesQuery.trim().toLowerCase();
    if (!query) return choices;
    return choices.filter((choice) =>
      String(getChoiceDisplayTitle(choice, menuPrimaryLanguage) || "")
        .toLowerCase()
        .includes(query)
    );
  }, [choices, choicesQuery, menuPrimaryLanguage]);

  const livePreview = useMemo(() => {
    const assignedIds = editingChoiceId ? choiceItems[editingChoiceId] || [] : [];
    const itemsById = new Map(items.map((item) => [item.tempId, item]));
    const previewItems = assignedIds
      .map((itemId) => itemsById.get(itemId))
      .filter(Boolean)
      .map((item) => ({
        id: item.tempId,
        title: getItemDisplayTitle(item, menuPrimaryLanguage),
        price: item.price ?? 0,
        havePrice: item.havePrice !== false,
      }));
    const min = Number(minSelected || 0);
    const trimmed = String(title || "").trim();
    return {
      ...buildChoiceFormLivePreview({
        titles: { en: trimmed, [menuPrimaryLanguage]: trimmed },
        minSelected: min,
        maxSelected: Number(maxSelected || 0),
        obligatory: min > 0,
        items: previewItems,
      }),
      editingChoiceId: editingChoiceId || null,
      assignProductIds: [...assignProductIds],
    };
  }, [
    title,
    minSelected,
    maxSelected,
    editingChoiceId,
    choiceItems,
    items,
    menuPrimaryLanguage,
    assignProductIds,
  ]);

  // Choices step: parent opens the untitled product-detail preview first.
  // Back on the phone returns to the menu list so a real product can be opened.
  useLayoutEffect(() => {
    setLiveDetailCandidate?.(livePreview);
    return undefined;
  }, [livePreview, setLiveDetailCandidate]);

  const toggleChoiceSelection = (choiceId) => {
    setSelectedChoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(choiceId)) next.delete(choiceId);
      else next.add(choiceId);
      return next;
    });
  };

  const getAssignableChoiceIds = (productId, sourceIds) => {
    const assignedOnProduct = new Set(productChoices[productId] || []);
    return [...new Set((sourceIds || []).filter(Boolean))].filter(
      (choiceId) => !assignedOnProduct.has(choiceId)
    );
  };

  const assignChoicesToProduct = (productId, choiceIds) => {
    const uniqueIds = getAssignableChoiceIds(productId, choiceIds);
    if (!uniqueIds.length || !productId) return;

    setDraft((prev) => {
      const rel = prev.relations || {};
      const nextProductChoices = { ...(rel.productChoices || {}) };
      const current = new Set(nextProductChoices[productId] || []);
      uniqueIds.forEach((id) => current.add(id));
      nextProductChoices[productId] = [...current];
      return {
        ...prev,
        relations: { ...rel, productChoices: nextProductChoices },
      };
    });
    showWizardToast(t, "wizardChoicesAssigned", { count: uniqueIds.length });
    setSelectedChoiceIds(new Set());
  };

  const unassignChoiceFromProduct = (productId, choiceId) => {
    setDraft((prev) => {
      const rel = prev.relations || {};
      const nextProductChoices = { ...(rel.productChoices || {}) };
      nextProductChoices[productId] = (nextProductChoices[productId] || []).filter(
        (id) => id !== choiceId
      );
      return {
        ...prev,
        relations: { ...rel, productChoices: nextProductChoices },
      };
    });
  };

  const minValue = Number(minSelected || 0);
  const maxValue = Number(maxSelected || 0);
  const minMaxValid = Number.isFinite(minValue) && Number.isFinite(maxValue) && minValue < maxValue;
  const canSave = Boolean(title.trim()) && maxValue > 0 && minMaxValid;

  const resetChoiceForm = () => {
    setTitle("");
    setMinSelected("0");
    setMaxSelected("1");
    setEditingChoiceId(null);
    setAssignProductIds(new Set());
    setAssignProductQuery("");
    setAssignDropdownOpen(false);
  };

  const toggleAssignProduct = (productId) => {
    if (!productId) return;
    setAssignProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const setAssignPreviewProduct = (checked) => {
    if (!livePreviewProductId) return;
    setAssignProductIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(livePreviewProductId);
      else next.delete(livePreviewProductId);
      return next;
    });
  };

  useEffect(() => {
    if (!assignDropdownOpen) return undefined;
    const onPointerDown = (event) => {
      if (!assignDropdownRef.current?.contains(event.target)) {
        setAssignDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [assignDropdownOpen]);

  useEffect(() => {
    if (!editingChoiceId || !choiceFormRef.current) return;
    choiceFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    document.getElementById("wizard-choice-title")?.focus({ preventScroll: true });
  }, [editingChoiceId]);

  const startEditChoice = (choice) => {
    setEditingChoiceId(choice.tempId);
    setTitle(getChoiceDisplayTitle(choice, menuPrimaryLanguage) || "");
    setMinSelected(String(choice.minSelected ?? 0));
    setMaxSelected(String(choice.maxSelected ?? 1));
    setAssignProductIds(new Set(getProductIdsForChoice(choice.tempId, productChoices)));
  };

  const saveChoice = () => {
    if (!title.trim()) {
      showWizardValidation(t, "wizardChoiceTitleRequired");
      return;
    }

    const min = Number(minSelected || 0);
    const max = Number(maxSelected || 0);
    if (max <= 0) {
      showWizardValidation(t, "wizardChoiceMaxRequired");
      return;
    }
    if (!(min < max)) {
      showWizardValidation(t, "wizardChoiceMinMaxInvalid");
      return;
    }

    const choiceDetails = buildChoiceLocalizedDetails({
      primaryLanguage: menuPrimaryLanguage,
      optionalLanguages: menuOptionalLanguages,
      titles: { [menuPrimaryLanguage]: title },
    });

    const choicePayload = {
      minSelected: min,
      maxSelected: max,
      obligatory: min > 0,
      isFree: false,
      choiceDetails,
    };
    const selectedAssignIds = [...assignProductIds];

    if (editingChoiceId) {
      setDraft((prev) => {
        const rel = prev.relations || {};
        return {
          ...prev,
          choices: (prev.choices || []).map((choice) =>
            choice.tempId === editingChoiceId ? { ...choice, ...choicePayload } : choice
          ),
          relations: {
            ...rel,
            productChoices: syncChoiceProductAssignments(
              rel.productChoices,
              editingChoiceId,
              selectedAssignIds
            ),
          },
        };
      });
      showWizardToast(
        t,
        selectedAssignIds.length ? "wizardChoiceUpdatedAndAssigned" : "wizardChoiceUpdated",
        selectedAssignIds.length ? { count: selectedAssignIds.length } : undefined
      );
    } else {
      const newChoiceId = makeTempId("choice");
      setDraft((prev) => {
        const rel = prev.relations || {};
        const nextProductChoices = selectedAssignIds.length
          ? syncChoiceProductAssignments(rel.productChoices, newChoiceId, selectedAssignIds)
          : rel.productChoices || {};
        return {
          ...prev,
          choices: [
            ...(Array.isArray(prev.choices) ? prev.choices : []),
            {
              tempId: newChoiceId,
              ...choicePayload,
            },
          ],
          relations: selectedAssignIds.length
            ? { ...rel, productChoices: nextProductChoices }
            : prev.relations,
        };
      });
      showWizardToast(
        t,
        selectedAssignIds.length ? "wizardChoiceAddedAndAssigned" : "wizardChoiceAdded",
        selectedAssignIds.length ? { count: selectedAssignIds.length } : undefined
      );
    }

    resetChoiceForm();
  };

  const removeChoice = (choiceTempId) => {
    if (editingChoiceId === choiceTempId) {
      resetChoiceForm();
    }
    setDraft((prev) => {
      const next = { ...prev };
      next.choices = (prev.choices || []).filter((c) => c.tempId !== choiceTempId);

      const itemTempIdsToRemove = Array.isArray(prev.relations?.choiceItems?.[choiceTempId])
        ? prev.relations.choiceItems[choiceTempId]
        : [];
      next.items = (prev.items || []).filter((it) => !itemTempIdsToRemove.includes(it.tempId));

      const nextRelations = { ...(prev.relations || {}) };
      nextRelations.choiceItems = { ...(nextRelations.choiceItems || {}) };
      delete nextRelations.choiceItems[choiceTempId];

      nextRelations.productChoices = { ...(nextRelations.productChoices || {}) };
      for (const [productTempId, choiceTempIds] of Object.entries(nextRelations.productChoices)) {
        if (!Array.isArray(choiceTempIds)) continue;
        const filtered = choiceTempIds.filter((id) => id !== choiceTempId);
        if (filtered.length) nextRelations.productChoices[productTempId] = filtered;
        else delete nextRelations.productChoices[productTempId];
      }

      next.relations = nextRelations;
      return next;
    });
    showWizardToast(t, "wizardChoiceDeleted");
  };

  return (
    <>
      <div className="mt-3 start-step-content">
        <div className="start-step-hero">
          <h2 className="start-step-title">
            <span className="step-badge">5</span>
            {t("wizardChoicesHeading")}
          </h2>
          <p className="start-step-subtitle">{t("wizardChoicesSub")}</p>
        </div>

        <div className="start-options">
          <section
            className="start-products-form start-step-hero-scroll-target"
            ref={choiceFormRef}
            aria-label={editingChoiceId ? t("wizardEditChoiceLabel") : t("wizardAddChoiceLabel")}
          >
            <div className="start-options-section-head">
              <span className="start-options-section-head__index">1</span>
              <div>
                <h3 className="start-options-section-head__title">
                  {editingChoiceId ? t("wizardEditChoiceLabel") : t("wizardAddChoiceLabel")}
                </h3>
                <p className="start-options-section-head__hint">{t("wizardChoicesSub")}</p>
              </div>
            </div>
            <div className="start-choice-fields">
              <div className="start-choice-fields__title">
                <label className="start-step1-label" htmlFor="wizard-choice-title">
                  {t("wizardChoiceTitleLabel")}
                  <span className="start-step1-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="wizard-choice-title"
                  className="form-control start-input"
                  placeholder={t("wizardChoiceTitlePlaceholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="start-step1-label" htmlFor="wizard-choice-min">
                  {t("wizardChoiceMinLabel")}
                </label>
                <input
                  id="wizard-choice-min"
                  type="number"
                  min="0"
                  max={Math.max(0, maxValue - 1)}
                  className="form-control start-input"
                  value={minSelected}
                  onChange={(e) => setMinSelected(e.target.value)}
                />
              </div>
              <div>
                <label className="start-step1-label" htmlFor="wizard-choice-max">
                  {t("wizardChoiceMaxLabel")}
                </label>
                <input
                  id="wizard-choice-max"
                  type="number"
                  min={Math.max(1, minValue + 1)}
                  className="form-control start-input"
                  value={maxSelected}
                  onChange={(e) => setMaxSelected(e.target.value)}
                />
              </div>
            </div>
            {!minMaxValid && maxValue > 0 ? (
              <p className="start-choice-minmax-hint" role="alert">
                {t("wizardChoiceMinMaxInvalid")}
              </p>
            ) : null}
            {showAssignToSelectedProduct ? (
              <label className="start-choice-assign-preview" htmlFor="wizard-choice-assign-preview">
                <input
                  id="wizard-choice-assign-preview"
                  type="checkbox"
                  className="start-choice-assign-preview__input"
                  checked={assignProductIds.has(livePreviewProductId)}
                  onChange={(e) => setAssignPreviewProduct(e.target.checked)}
                />
                <span className="start-choice-assign-preview__text">
                  {t("wizardChoiceAssignToSelectedProductBefore")}{" "}
                  <strong>{selectedPreviewProduct.title}</strong>
                </span>
              </label>
            ) : null}
            {productOptions.length > 0 ? (
              <div className="start-choice-assign" ref={assignDropdownRef}>
                <div className="start-choice-assign__head">
                  <label className="start-step1-label" htmlFor="wizard-choice-assign-search">
                    {t("wizardChoiceAssignProductsLabel")}
                  </label>
                  <span className="start-choice-assign__optional">{t("optional")}</span>
                </div>
                {selectedAssignProducts.length > 0 ? (
                  <div className="start-choice-assign__cards" aria-live="polite">
                    {selectedAssignProducts.map((product) => (
                      <span key={product.id} className="start-choice-assign__card">
                        <span className="start-choice-assign__card-name">{product.title}</span>
                        <button
                          type="button"
                          className="start-choice-assign__card-remove"
                          onClick={() => toggleAssignProduct(product.id)}
                          aria-label={t("wizardChoiceAssignRemoveProduct", {
                            product: product.title,
                          })}
                          title={t("remove") || "Remove"}
                        >
                          <i className="fa fa-times" aria-hidden="true" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
                {moreProductOptions.length > 0 ? (
                  <div className={`start-choice-assign__combobox${assignDropdownOpen ? " is-open" : ""}`}>
                    <div className="start-choice-assign__search">
                      <i className="fa fa-search start-choice-assign__search-icon" aria-hidden="true" />
                      <input
                        id="wizard-choice-assign-search"
                        type="search"
                        className="start-choice-assign__search-input"
                        placeholder={t("wizardChoiceAssignProductsPlaceholder")}
                        value={assignProductQuery}
                        onChange={(e) => {
                          setAssignProductQuery(e.target.value);
                          setAssignDropdownOpen(true);
                        }}
                        onFocus={() => setAssignDropdownOpen(true)}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="start-choice-assign__search-toggle"
                        onClick={() => setAssignDropdownOpen((open) => !open)}
                        aria-expanded={assignDropdownOpen}
                        aria-label={t("wizardChoiceAssignProductsLabel")}
                      >
                        <i className={`fa fa-chevron-${assignDropdownOpen ? "up" : "down"}`} aria-hidden="true" />
                      </button>
                    </div>
                    {assignDropdownOpen ? (
                      <div className="start-choice-assign__menu" role="listbox" aria-multiselectable="true">
                        {filteredMoreProducts.length === 0 ? (
                          <p className="start-choice-assign__menu-empty">
                            {t("wizardChoiceAssignProductsNoResults")}
                          </p>
                        ) : (
                          filteredMoreProducts.map((product) => {
                            const selected = assignProductIds.has(product.id);
                            return (
                              <label
                                key={product.id}
                                className={`start-choice-assign__option${selected ? " is-selected" : ""}`}
                              >
                                <input
                                  type="checkbox"
                                  className="start-choice-assign__option-input"
                                  checked={selected}
                                  onChange={() => toggleAssignProduct(product.id)}
                                />
                                <span className="start-choice-assign__option-label">{product.title}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="start-choice-assign__empty">{t("wizardChoiceAssignNoProducts")}</p>
            )}
            <div className="start-products-form__actions">
              <button type="button" className="start-static-btn" onClick={resetChoiceForm}>
                {t("cancel")}
              </button>
              <button
                type="button"
                className="start-products-add"
                disabled={!canSave}
                onClick={saveChoice}
              >
                {editingChoiceId ? t("wizardUpdateChoice") : t("wizardAddChoice")}
              </button>
            </div>
          </section>

          <section
            className={`start-products-added${choicesOpen ? " is-open" : ""}`}
            aria-label={t("wizardAllChoices")}
          >
            <button
              type="button"
              className="start-products-added__toggle"
              onClick={() => setChoicesOpen((open) => !open)}
              aria-expanded={choicesOpen}
            >
              <span className="start-options-section-head__index">2</span>
              <h3 className="start-products-added__title">{t("wizardAllChoices")}</h3>
              <span className="start-products-added__count">{choices.length}</span>
              <span className="start-products-added__chevron" aria-hidden="true">
                <i className="fa fa-chevron-down" />
              </span>
            </button>
            {choicesOpen ? (
              <div className="start-products-added__body">
                <p className="start-options-hint">{t("wizardChoicesMultiProductHint")}</p>
                {choices.length === 0 ? (
                  <p className="start-products-empty">{t("wizardNoChoicesYet")}</p>
                ) : (
                  <div className="start-choice-library">
                    <div className="start-choice-library__search">
                      <i className="fa fa-search start-choice-library__search-icon" aria-hidden="true" />
                      <input
                        id="wizard-choices-search"
                        type="search"
                        className="start-choice-library__search-input"
                        placeholder={t("wizardChoiceSearchPlaceholder")}
                        value={choicesQuery}
                        onChange={(e) => setChoicesQuery(e.target.value)}
                        autoComplete="off"
                        aria-label={t("wizardChoiceSearchPlaceholder")}
                      />
                      {choicesQuery ? (
                        <button
                          type="button"
                          className="start-choice-library__search-clear"
                          onClick={() => setChoicesQuery("")}
                          aria-label={t("clear") || "Clear"}
                          title={t("clear") || "Clear"}
                        >
                          <i className="fa fa-times" aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                    <div className="start-choice-library__meta">
                      <span>
                        {choicesQuery.trim()
                          ? t("wizardChoiceSearchResults", {
                              count: filteredChoices.length,
                              total: choices.length,
                            })
                          : t("wizardChoiceSearchTotal", { count: choices.length })}
                      </span>
                    </div>
                    {filteredChoices.length === 0 ? (
                      <p className="start-choice-library__empty">{t("wizardChoiceSearchNoResults")}</p>
                    ) : (
                      <ul className="start-choice-list start-choice-list--scroll">
                        {filteredChoices.map((choice) => {
                          const isSelected = selectedChoiceIds.has(choice.tempId);
                          const productCount = countProductsForChoice(choice.tempId, productChoices);
                          return (
                            <li
                              key={choice.tempId}
                              className={`start-choice-card${isSelected ? " is-selected" : ""}${
                                editingChoiceId === choice.tempId ? " is-editing" : ""
                              }`}
                            >
                              <button
                                type="button"
                                className="start-choice-card__select"
                                onClick={() => toggleChoiceSelection(choice.tempId)}
                              >
                                <span className={`start-choice-card__check${isSelected ? " is-on" : ""}`}>
                                  {isSelected ? <i className="fa fa-check" aria-hidden="true" /> : null}
                                </span>
                                <span className="start-choice-card__info">
                                  <span className="start-choice-card__title">
                                    {getChoiceDisplayTitle(choice, menuPrimaryLanguage)}
                                  </span>
                                  <span className="start-choice-card__pills">
                                    <span className="start-choice-card__pill">
                                      {t("wizardChoiceMinLabel")} {choice.minSelected ?? 0}
                                    </span>
                                    <span className="start-choice-card__pill">
                                      {t("wizardChoiceMaxLabel")} {choice.maxSelected ?? 1}
                                    </span>
                                    {productCount > 0 ? (
                                      <span className="start-choice-card__pill is-count">
                                        {t("wizardChoiceProductCount", { count: productCount })}
                                      </span>
                                    ) : null}
                                  </span>
                                </span>
                              </button>
                              <div className="start-products-row__actions">
                                <button
                                  type="button"
                                  className="start-products-row__icon-btn is-edit"
                                  onClick={() => startEditChoice(choice)}
                                  aria-label={t("edit")}
                                  title={t("edit")}
                                >
                                  <i className="fa fa-edit" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="start-products-row__icon-btn is-danger"
                                  onClick={() => removeChoice(choice.tempId)}
                                  aria-label={t("remove")}
                                  title={t("remove")}
                                >
                                  <i className="fa fa-trash-alt" aria-hidden="true" />
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </section>

          <section className="start-products-added" aria-label={t("wizardAssignChoicesToProducts")}>
            <div className="start-options-section-head">
              <span className="start-options-section-head__index">3</span>
              <h3 className="start-products-added__title">{t("wizardAssignChoicesToProducts")}</h3>
              {selectedChoiceIds.size > 0 ? (
                <span className="start-products-added__count">{selectedChoiceIds.size}</span>
              ) : null}
            </div>
            {selectedChoiceIds.size > 0 ? (
              <p className="start-assign-banner">
                {t("wizardAssignChoicesBatch", { count: selectedChoiceIds.size })}
              </p>
            ) : (
              <p className="start-options-hint">{t("wizardChoicesMultiProductHint")}</p>
            )}
            {products.length === 0 ? (
              <p className="start-products-empty">{t("wizardNoProductsToLink")}</p>
            ) : (
              <div className="start-assign-list">
                {products.map((product) => {
                  const assignedIds = productChoices[product.tempId] || [];
                  const hasSelection = selectedChoiceIds.size > 0;
                  const sourceIds = [...selectedChoiceIds];
                  const pendingIds = hasSelection
                    ? getAssignableChoiceIds(product.tempId, sourceIds)
                    : [];
                  const canAssign = hasSelection && pendingIds.length > 0;

                  return (
                    <article key={product.tempId} className="start-assign-card">
                      <div className="start-assign-card__head">
                        <div className="start-assign-card__identity">
                          <span className="start-assign-card__name">
                            {getProductDisplayTitle(product, menuPrimaryLanguage)}
                          </span>
                          <span className="start-assign-card__price">
                            {formatMoney(product.price ?? 0)}
                          </span>
                        </div>
                        {choices.length > 0 ? (
                          <button
                            type="button"
                            className="start-assign-btn"
                            disabled={!canAssign}
                            onClick={() => assignChoicesToProduct(product.tempId, sourceIds)}
                          >
                            {!hasSelection
                              ? t("wizardAssignChoicesBatch", { count: 0 })
                              : pendingIds.length > 0
                                ? t("wizardAssignChoicesBatch", { count: pendingIds.length })
                                : t("wizardChoicesAlreadyOnProduct")}
                          </button>
                        ) : null}
                      </div>
                      <div className="start-assign-chips">
                        {assignedIds.length === 0 ? (
                          <p className="start-assign-empty">{t("wizardNoChoicesYet")}</p>
                        ) : (
                          assignedIds.map((choiceId) => {
                            const choice = choices.find((c) => c.tempId === choiceId);
                            if (!choice) return null;
                            const minSelected = choice.minSelected ?? 0;
                            const maxSelected = choice.maxSelected ?? 1;
                            return (
                              <span key={choiceId} className="start-assign-chip">
                                <i className="fa fa-list-check" aria-hidden="true" />
                                <span className="start-assign-chip__label">
                                  {getChoiceDisplayTitle(choice, menuPrimaryLanguage)}
                                </span>
                                <span className="start-assign-chip__meta">
                                  {minSelected}–{maxSelected}
                                </span>
                                <button
                                  type="button"
                                  className="start-assign-chip__remove"
                                  onClick={() => unassignChoiceFromProduct(product.tempId, choiceId)}
                                  aria-label={t("remove")}
                                  title={t("remove")}
                                >
                                  <i className="fa fa-times" aria-hidden="true" />
                                </button>
                              </span>
                            );
                          })
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <button className="btn btn-secondary sw-btn-prev me-1 start-btn-back" onClick={goPrev} type="button">
          ← {t("back")}
        </button>
        <div className="wizard-step-counter">{t("wizardStep5of9")}</div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary sw-btn-skip" onClick={goNext} type="button">
            {t("wizardSkipStep")}
          </button>
          <button className="btn btn-primary sw-btn-next start-btn-next" onClick={goNext} type="button">
            {t("wizardContinue")} <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Step3Choices;
